export type TransitionTable<S extends string> = Readonly<Record<S, readonly S[]>>;

export type StateChangeListener<S extends string> = (from: S, to: S) => void;

/**
 * Minimal finite state machine with an explicit transition table.
 * Illegal transitions throw – they always indicate a logic bug and must not
 * be silently ignored.
 */
export class StateMachine<S extends string> {
  private current: S;
  private elapsed = 0;
  private readonly listeners = new Set<StateChangeListener<S>>();

  constructor(
    initial: S,
    private readonly transitions: TransitionTable<S>,
  ) {
    this.current = initial;
  }

  get state(): S {
    return this.current;
  }

  /** Seconds spent in the current state (advanced via update). */
  get timeInState(): number {
    return this.elapsed;
  }

  is(...states: S[]): boolean {
    return states.includes(this.current);
  }

  canTransition(to: S): boolean {
    return this.transitions[this.current].includes(to);
  }

  transition(to: S): void {
    if (to === this.current) return;
    if (!this.canTransition(to)) {
      throw new Error(`Illegal state transition ${this.current} → ${to}`);
    }
    const from = this.current;
    this.current = to;
    this.elapsed = 0;
    for (const listener of this.listeners) listener(from, to);
  }

  /** Bypasses the transition table – only for resets (new run). */
  reset(state: S): void {
    this.current = state;
    this.elapsed = 0;
  }

  update(dt: number): void {
    this.elapsed += dt;
  }

  onChange(listener: StateChangeListener<S>): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}
